import { useEffect, useState, memo } from "react";
import { getProtectionWarningForFeature } from "../../../utils/restrictedAreasUtils";
import ErrorMessage from "../../genericComponents/ErrorMessage/ErrorMessage";
import "./FeatureWarnings.css";
import type Graphic from "@arcgis/core/Graphic";

interface FeatureWarningsProps {
    feature: Graphic | null;
}

const FeatureWarnings = (props: FeatureWarningsProps) => {
    const { feature } = props;

    const [warning, setWarning] = useState<{ objectID: number; warning: string }>({ objectID: -1, warning: "" });

    useEffect(() => {
        if (warning.objectID !== feature?.attributes.objectid) {
            setWarning({ objectID: -1, warning: "" });
        }

        // Clear warning if no feature is selected
        if (!feature) {
            setWarning({ objectID: -1, warning: "" });
            return;
        }

        // Skip if we already have a warning for this feature
        if (warning.objectID === feature.attributes.objectid) {
            return;
        }

        // Create a debounce timeout
        const debounceTimeout = setTimeout(() => {
            getProtectionWarningForFeature(feature.geometry, feature.attributes.objectid)
                .then((returnedWarning) => {
                    if (returnedWarning) {
                        setWarning(returnedWarning);
                    } else {
                        setWarning({ objectID: feature.attributes.objectid, warning: "" });
                    }
                })
                .catch((error) => {
                    console.error("Error fetching protection warning:", error);
                    setWarning({ objectID: -1, warning: "" });
                });
        }, 300); // 300ms debounce time

        // Cleanup function to cancel the timeout if effect reruns before timeout completes
        return () => clearTimeout(debounceTimeout);
    }, [feature?.geometry, feature?.attributes.objectid, warning.objectID]);

    if (warning.warning === "" || warning.objectID === -1) {
        // No warning to display
        return null;
    }

    return (
        <div className="feature-warning-container">
            <ErrorMessage message={warning.warning} w100 />
        </div>
    );
};

export default memo(FeatureWarnings, (prevProps, nextProps) => {
    // Return true if props are equal (skip rerender), false if props changed (rerender)
    const prevFeature = prevProps.feature;
    const nextFeature = nextProps.feature;

    // Both null or undefined
    if (!prevFeature && !nextFeature) {
        return true;
    }

    // One is null, the other isn't
    if (!prevFeature || !nextFeature) {
        return false;
    }

    // Compare objectid
    if (prevFeature.attributes?.objectid !== nextFeature.attributes?.objectid) {
        return false;
    }

    // Compare geometry by reference (ArcGIS typically maintains stable geometry references)
    // If geometries are the same reference, no need to rerender
    if (prevFeature.geometry === nextFeature.geometry) {
        return true;
    }

    // If geometry references differ, compare their JSON representation
    // This handles cases where geometry objects are recreated but contain the same data
    try {
        return JSON.stringify(prevFeature.geometry?.toJSON()) === JSON.stringify(nextFeature.geometry?.toJSON());
    } catch {
        // If comparison fails, allow rerender to be safe
        return false;
    }
});
